import { Config, DestroyReason, DriveStep, Driver, driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useCallback, useEffect, useRef, useState } from "react";

export type { DriveStep, DriverHook, HookOpts } from "driver.js";

/**
 * CLE-3015 — the thin driver.js layer under `useTour`. The only module (with useTour's types) that
 * imports driver.js. Born as the PR 0 spike's hook; the spike's tests moved with it.
 *
 * - `allowClose: false` + `overlayClickBehavior: "none"`: Escape and overlay clicks never end the tour;
 *   only the popover's own (React) Exit does, via `destroy`.
 * - `onPopoverRender` puts an empty element in the popover's description (replacing driver's placeholder
 *   text when the step has no description of its own) and hands it back as
 *   `popoverContainer`, so the caller can `createPortal` React content into it. driver.js builds a new
 *   popover for every step, so the container changes per step and the old portal unmounts.
 * - The driver instance is destroyed on unmount, which also cancels a pending `waitForElement` (driver
 *   clears its timeout and disconnects its MutationObserver).
 * - `hooks` are passed through to driver.js (read through a ref, so inline callbacks do not restart it).
 *   With `onNextClick` set, both the Next button and an `advanceOnClick` click call it instead of moving
 *   on; with `onDestroyStarted` set, driver.js calls it instead of tearing down — including when it runs
 *   off the end of the steps because every remaining target was missing, the one case `onDestroyed`
 *   never reports — and the hook must call `destroy()` itself.
 * - `runKey` restarts the driver with the current `steps` / `startIndex` while `enabled` stays true.
 */
export type IDriverHooks = Pick<
  Config,
  "onHighlightStarted" | "onNextClick" | "onDestroyStarted"
>;

export interface IUseDriverTourOptions {
  steps: DriveStep[];
  enabled: boolean;
  startIndex?: number;
  runKey?: number | string;
  onDestroyed?: (reason: DestroyReason | undefined) => void;
  hooks?: IDriverHooks;
  popoverClass?: string;
}

export interface IUseDriverTourResult {
  popoverContainer: HTMLElement | null;
  destroy: () => void;
  moveTo: (index: number) => void;
  getActiveIndex: () => number | undefined;
}

export function useDriverTour({
  steps,
  enabled,
  startIndex = 0,
  runKey,
  onDestroyed,
  hooks,
  popoverClass,
}: IUseDriverTourOptions): IUseDriverTourResult {
  const [popoverContainer, setPopoverContainer] = useState<HTMLElement | null>(
    null
  );
  const driverRef = useRef<Driver | null>(null);
  // Read through refs so a caller passing inline arrays/callbacks does not restart the tour.
  const stepsRef = useRef(steps);
  stepsRef.current = steps;
  const startIndexRef = useRef(startIndex);
  startIndexRef.current = startIndex;
  const onDestroyedRef = useRef(onDestroyed);
  onDestroyedRef.current = onDestroyed;
  const hooksRef = useRef(hooks);
  hooksRef.current = hooks;

  useEffect(() => {
    if (!enabled) return undefined;

    const hookConfig: Partial<Config> = {};
    if (hooksRef.current?.onHighlightStarted) {
      hookConfig.onHighlightStarted = (...args) =>
        hooksRef.current?.onHighlightStarted?.(...args);
    }
    if (hooksRef.current?.onNextClick) {
      hookConfig.onNextClick = (...args) =>
        hooksRef.current?.onNextClick?.(...args);
    }
    if (hooksRef.current?.onDestroyStarted) {
      hookConfig.onDestroyStarted = (...args) =>
        hooksRef.current?.onDestroyStarted?.(...args);
    }

    const instance = driver({
      steps: stepsRef.current,
      allowClose: false,
      overlayClickBehavior: "none",
      ...(popoverClass ? { popoverClass } : {}),
      ...hookConfig,
      onPopoverRender: (popover, { state }) => {
        const container = document.createElement("div");
        container.className = "tour-react-content";
        // driver.js builds the description and title with placeholder text ("Popover description is
        // here", "Popover Title") and overwrites them only when the step has text of its own. The
        // engine's steps never do (the content is React), so the placeholders were showing above our
        // content (dev feedback round 1). Replace them; keep text a step did set.
        const stepPopover = state.activeStep?.popover;
        if (stepPopover?.description) {
          popover.description.appendChild(container);
        } else {
          popover.description.replaceChildren(container);
        }
        if (!stepPopover?.title) popover.title.replaceChildren();
        // Shown even when a step has no description text of its own.
        popover.description.style.display = "block";
        setPopoverContainer(container);
      },
      // driver.js sizes and places the popover inside `onPopoverRender`, before React has
      // committed the caller's portal content into it. A `side: "bottom"` popover is anchored by
      // `style.bottom`, so that late content grows it UPWARD over the highlighted element (Dev QA
      // of #1432), and driver only repositions on resize/scroll. Re-place it once the highlight
      // transition ends (`duration`, 400 ms), by which time the portal has long committed.
      // `refresh()` is a no-op before then: it reads driver's `__activeElement`, set only at the end.
      // Driver-level, so it runs for every step of every segment the engine drives (CLE-3015 PR 2
      // carries #1434's fix forward into the moved hook).
      onHighlighted: () => instance.refresh(),
      onDestroyed: (_element, _step, opts) => {
        setPopoverContainer(null);
        onDestroyedRef.current?.(opts.reason);
      },
    });
    driverRef.current = instance;
    instance.drive(startIndexRef.current);

    return () => {
      instance.destroy();
      if (driverRef.current === instance) driverRef.current = null;
      setPopoverContainer(null);
    };
  }, [enabled, runKey, popoverClass]);

  const destroy = useCallback(() => {
    driverRef.current?.destroy();
    setPopoverContainer(null);
  }, []);

  const moveTo = useCallback((index: number) => {
    driverRef.current?.moveTo(index);
  }, []);

  const getActiveIndex = useCallback(
    () => driverRef.current?.getActiveIndex(),
    []
  );

  return { popoverContainer, destroy, moveTo, getActiveIndex };
}
